import { useId } from 'react';
import {
    type AeroPartialExitDraft,
    createDefaultManualPartialExit
} from '@/features/tradeLogicAuthoring/components/aeroOptimizationJobInput';

interface PartialExitEditorProps {
    levels: AeroPartialExitDraft[];
    onChange: (levels: AeroPartialExitDraft[]) => void;
    disabled?: boolean;
}

const rowInputClass =
    'h-[34px] w-[72px] rounded-md border border-[#444] bg-[#1E1E1E] px-2 text-sm text-zinc-200 outline-none focus:border-[#888]';

const PartialExitEditor = ({ levels, onChange, disabled = false }: PartialExitEditorProps) => {
    const exitTotal = levels.reduce((sum, level) => {
        const exit = Number(level.exitPercentage);

        return Number.isFinite(exit) && exit > 0 ? sum + exit : sum;
    }, 0);

    const updateLevel = (index: number, changes: Partial<AeroPartialExitDraft>) => {
        onChange(levels.map((level, levelIndex) => (levelIndex === index ? { ...level, ...changes } : level)));
    };

    const removeLevel = (index: number) => {
        onChange(levels.filter((_, levelIndex) => levelIndex !== index));
    };

    const addLevel = () => {
        onChange([...levels, createDefaultManualPartialExit()]);
    };

    return (
        <div className="space-y-2">
            {levels.length === 0 && (
                <div className="rounded-md border border-dashed border-zinc-700 bg-black/20 px-3 py-2.5 text-sm text-zinc-500">
                    No partial-exit levels yet. Add one to scale out of trades as profit builds.
                </div>
            )}

            {levels.map((level, index) => (
                <div
                    className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 bg-black/20 px-3 py-2 text-sm text-zinc-300"
                    key={index}
                >
                    <PartialExitRowFields disabled={disabled} level={level} onChange={(changes) => updateLevel(index, changes)} />
                    <button
                        aria-label={`Remove partial exit ${index + 1}`}
                        className="ml-auto flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-red-300 disabled:opacity-40"
                        disabled={disabled}
                        type="button"
                        onClick={() => removeLevel(index)}
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                        </svg>
                    </button>
                </div>
            ))}

            <div className="flex flex-wrap items-center justify-between gap-2">
                <button
                    className="rounded-md border border-dashed border-zinc-600 px-3 py-1.5 text-sm font-medium text-cyan-400 hover:border-cyan-500 hover:text-cyan-300 disabled:opacity-40"
                    disabled={disabled}
                    type="button"
                    onClick={addLevel}
                >
                    + Add partial-exit level
                </button>
                {exitTotal > 100 && (
                    <span className="text-xs text-amber-300">Levels sell {exitTotal}% of the position — more than 100%.</span>
                )}
            </div>
        </div>
    );
};

const PartialExitRowFields = ({
    level,
    disabled,
    onChange
}: {
    level: AeroPartialExitDraft;
    disabled: boolean;
    onChange: (changes: Partial<AeroPartialExitDraft>) => void;
}) => {
    const atProfitId = useId();
    const exitId = useId();

    return (
        <>
            <label className="text-zinc-500" htmlFor={atProfitId}>
                At
            </label>
            <input
                className={rowInputClass}
                disabled={disabled}
                id={atProfitId}
                inputMode="decimal"
                placeholder="8"
                value={level.atProfit}
                onChange={(event) => onChange({ atProfit: event.target.value })}
            />
            <span className="text-zinc-500">% profit, sell</span>
            <input
                className={rowInputClass}
                disabled={disabled}
                id={exitId}
                inputMode="decimal"
                placeholder="25"
                value={level.exitPercentage}
                onChange={(event) => onChange({ exitPercentage: event.target.value })}
            />
            <span className="text-zinc-500">% of the position</span>
        </>
    );
};

export default PartialExitEditor;
